import { Link, useNavigate, useParams } from "react-router";
import Logo from "../../assets/texpress.png";
import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeClosed, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { joinViaInvite } from "@/services/Merchant.service";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PasswordError } from "@/components/PasswordError";
import libphonenumber from "google-libphonenumber";

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

export const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address!" }),
  password: z
    .string()
    .nonempty({
      message: "Password must meet the criteria's below:",
    })
    .refine(
      (val) => {
        try {
          const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[ -~]{12,}$/;
          // const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12,}$/;
          return regex.test(val);
        } catch (error) {
          return false;
        }
      },
      {
        message: "Password must meet the criteria's below:",
      }
    ),
  firstName: z.string().min(3, {
    message: "First name must be at least 3 characters long",
  }),
  lastName: z.string().min(3, {
    message: "Last name must be at least 3 characters long",
  }),
  phone: z
    .string()
    .nonempty({
      message: "Please phone number is required!",
    })
    .refine(
      (number) => {
        try {
          const phoneNumber = phoneUtil.parse(number);
          return phoneUtil.isValidNumber(phoneNumber);
        } catch (error) {
          return false;
        }
      },
      { message: "Please enter a valid phone number! " }
    ),
});

const Join = () => {
  const { token } = useParams();

  console.log(token);
  const [show, setShow] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      phone: "",
    },
  });
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: (data: any) => joinViaInvite(token, data),
    mutationKey: ["join_deeplink"],
    onSuccess: (res) => {
      navigate("/teams/invite/fallback", {
        state: {
          status: true,
        },
      });
    },
    onError: (err: any) => {
      navigate("/teams/invite/fallback", {
        state: {
          status: false,
          error: err.response?.data?.message || err.message,
        },
      });
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    await mutation.mutate(data);
  };

  return (
    <div className="flex flex-col  items-center justify-center gap-6 min-h-svh bg-muted ">
      <div className="flex flex-col gap-6">
        <Link to="/" className="flex items-center justify-center h-10">
          <img src={Logo} className="h-10" />
        </Link>

        <div className={cn("flex flex-col gap-6")}>
          <Card className="w-full lg:w-[542px] p-6">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-medium">Join team</CardTitle>
              <CardDescription>
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="grid gap-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <div
                              className={cn(
                                "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                                form.formState.errors?.firstName === undefined
                                  ? "border-input-border"
                                  : "border-destructive"
                              )}
                            >
                              <div className="flex flex-col w-full">
                                <FormLabel className="text-xs">
                                  First Name
                                </FormLabel>

                                <FormControl>
                                  <Input
                                    type="text"
                                    className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                            </div>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <div
                              className={cn(
                                "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                                form.formState.errors?.lastName === undefined
                                  ? "border-input-border"
                                  : "border-destructive"
                              )}
                            >
                              <div className="flex flex-col w-full">
                                <FormLabel className="text-xs">
                                  Last Name
                                </FormLabel>

                                <FormControl>
                                  <Input
                                    type="text"
                                    className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                            </div>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  </div>
                  <div className="grid gap-5">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <div
                              className={cn(
                                "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                                form.formState.errors?.email === undefined
                                  ? "border-input-border"
                                  : "border-destructive"
                              )}
                            >
                              <div className="flex flex-col w-full">
                                <FormLabel className="text-xs">Email</FormLabel>

                                <FormControl>
                                  <Input
                                    type="email"
                                    className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <div className="w-4 h-4 text-muted-foreground">
                                <Mail className="w-5 h-5" />
                              </div>
                            </div>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <div
                              className={cn(
                                "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                                form.formState.errors?.phone === undefined
                                  ? "border-input-border"
                                  : "border-destructive"
                              )}
                            >
                              <div className="flex flex-col w-full">
                                <FormLabel className="text-xs">
                                  Phone number
                                </FormLabel>

                                <FormControl>
                                  <Input
                                    type="text"
                                    className="w-full h-6 px-0 py-0 border-none outline-none placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                            </div>
                            <FormDescription>
                              E.g. +2340000000000
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <div
                              className={cn(
                                "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                                form.formState.errors?.password === undefined
                                  ? "border-input-border"
                                  : "border-destructive"
                              )}
                            >
                              <div className="flex flex-col w-full">
                                <FormLabel className="text-xs">
                                  Password
                                </FormLabel>

                                <FormControl>
                                  <Input
                                    type={!show ? "password" : "text"}
                                    placeholder=""
                                    className="w-full h-6 px-0 py-0 border-none outline-none placeholder:py-0 placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <div
                                className=" left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                                onClick={() => setShow(!show)}
                              >
                                {!show ? (
                                  <EyeClosed />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </div>
                            </div>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                    <PasswordError
                      passwordErrorMin={form.watch("password")}
                      passwordErrorLowerCaseChar={form.watch("password")}
                      passwordErrorUpperCaseChar={form.watch("password")}
                      passwordErrorHasNumber={form.watch("password")}
                      passwordErrorSpecialChar={form.watch("password")}
                    />

                    <Button
                      type="submit"
                      variant="default"
                      className="w-full bg-gradient-to-r from-primary to-primary-2 h-14 rounded-[10px] p-[10px]  ring-transparent focus-visible:ring-transparent"
                      disabled={
                        !form.formState.isDirty ||
                        !form.formState.isValid ||
                        form.formState.isSubmitting ||
                        mutation?.isPending ||
                        !form.getFieldState("password").isTouched
                        // || form.getFieldState("phone").isTouched
                        // || form.getFieldState("email").isTouched
                        // || form.getFieldState("firstName").isTouched
                        // || form.getFieldState("lastName").isTouched
                      }
                    >
                      {mutation?.isPending && (
                        <Loader2 className="text-white animate-spin" />
                      )}
                      Join Team
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Join;